import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { RefreshCw, Activity } from "lucide-react";

export const Route = createFileRoute("/admin/queue")({
  head: () => ({ meta: [{ title: "Bet Queue — Admin" }] }),
  component: QueuePage,
});

type Metrics = {
  queued: number;
  processing: number;
  dead: number;
  failed: number;
  completed_last_hour: number;
  oldest_queued_secs: number;
  avg_latency_ms: number;
  p95_latency_ms: number;
};

function fmtIST(ts: string) {
  return new Date(ts).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" });
}

function QueuePage() {
  const qc = useQueryClient();

  const { data: metrics, isLoading } = useQuery({
    queryKey: ["bet_queue_metrics"],
    refetchInterval: 5000,
    queryFn: async () => {
      const { data, error } = await supabase.rpc("bet_queue_metrics");
      if (error) throw error;
      return data as unknown as Metrics;
    },
  });

  const { data: problems } = useQuery({
    queryKey: ["bet_queue_problems"],
    refetchInterval: 10000,
    queryFn: async () => {
      const { data } = await supabase
        .from("bet_requests")
        .select("id, user_id, status, retry_count, entry_count, total_amount, last_error, submitted_at")
        .in("status", ["failed", "dead", "processing"])
        .order("submitted_at", { ascending: false })
        .limit(50);
      return data ?? [];
    },
  });

  const healthy = (metrics?.oldest_queued_secs ?? 0) < 30 && (metrics?.dead ?? 0) === 0;

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-xl font-black">Bet Queue Health</h2>
          <p className="text-xs text-muted-foreground">
            Live view of the background workers that accept and settle bets.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          className="gap-2"
          onClick={() => {
            qc.invalidateQueries({ queryKey: ["bet_queue_metrics"] });
            qc.invalidateQueries({ queryKey: ["bet_queue_problems"] });
          }}
        >
          <RefreshCw className="h-4 w-4" /> Refresh
        </Button>
      </div>

      <div
        className={`flex items-center gap-2 rounded-xl border p-3 text-sm font-bold ${
          healthy ? "border-primary/30 bg-primary/5 text-primary" : "border-destructive/40 bg-destructive/5 text-destructive"
        }`}
      >
        <Activity className="h-4 w-4" />
        {isLoading ? "Checking…" : healthy ? "All systems normal" : "Queue needs attention"}
      </div>

      <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
        <Stat label="Waiting" value={metrics?.queued ?? 0} />
        <Stat label="In Progress" value={metrics?.processing ?? 0} />
        <Stat label="Accepted (1h)" value={metrics?.completed_last_hour ?? 0} highlight />
        <Stat label="Needs Review" value={(metrics?.failed ?? 0) + (metrics?.dead ?? 0)} />
        <Stat label="Oldest Wait" value={`${metrics?.oldest_queued_secs ?? 0}s`} />
        <Stat label="Avg Speed" value={`${metrics?.avg_latency_ms ?? 0} ms`} />
        <Stat label="Slowest 5%" value={`${metrics?.p95_latency_ms ?? 0} ms`} />
        <Stat label="Dead Letters" value={metrics?.dead ?? 0} />
      </div>

      <div className="rounded-xl border bg-card">
        <div className="border-b p-3">
          <p className="text-sm font-black">Recent problem requests</p>
          <p className="text-[11px] text-muted-foreground">
            Anything stuck, retrying, or rejected. Empty is good.
          </p>
        </div>
        {(problems ?? []).length === 0 ? (
          <p className="p-6 text-center text-sm text-muted-foreground">Nothing to review.</p>
        ) : (
          <div className="divide-y">
            {(problems ?? []).map((p: any) => (
              <div key={p.id} className="p-3 text-xs">
                <div className="flex items-center justify-between gap-2">
                  <span className="font-bold uppercase">{p.status}</span>
                  <span className="text-muted-foreground">{fmtIST(p.submitted_at)} IST</span>
                </div>
                <p className="mt-1">
                  {p.entry_count} numbers · ₹{Number(p.total_amount).toLocaleString("en-IN")} · retries {p.retry_count}
                </p>
                {p.last_error && <p className="mt-1 text-destructive">{p.last_error}</p>}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, highlight }: { label: string; value: string | number; highlight?: boolean }) {
  return (
    <div className={`rounded-xl border p-3 ${highlight ? "border-primary/30 bg-primary/5" : "bg-card"}`}>
      <p className="text-[10px] font-bold uppercase text-muted-foreground">{label}</p>
      <p className={`mt-0.5 text-base font-black ${highlight ? "text-primary" : ""}`}>{value}</p>
    </div>
  );
}
